/**
 * L'état de jeu. Structure alignée sur le GDD §9 (partagé / par personnage).
 *
 * Règle : ce module est PUR (GDD §14.2). Aucun accès au DOM, à window,
 * au localStorage ni au réseau : il doit pouvoir tourner tel quel dans un
 * Worker le jour où le serveur devient autoritaire.
 */

import type { Porte, Slot } from "./objets.ts"

export type Element = "terre" | "feu" | "eau" | "air"

/** Ce qu'un personnage est en train de faire. Une seule activité à la fois (GDD §2). */
export type Activite =
  | { type: "combat"; familleId: number }
  | { type: "recolte"; ressourceId: number }
  | { type: "donjon"; donjonId: number }

/**
 * Une descente en cours, salle par salle.
 *
 * Le donjon était résolu d'un coup au clic : on lisait un compte rendu sans
 * avoir rien vu. Il se joue désormais en temps réel comme le farm, avec la vie
 * du monstre courant suivie pour de bon — c'est la seule façon de voir la barre
 * du boss descendre.
 */
export interface EnDescente {
  donjon: number
  /** numéro de salle, de 1 au nombre de salles */
  salle: number
  /** monstres encore debout dans la salle, celui de tête étant au contact */
  restants: number[]
  /** vie du monstre au contact, et sa vie de départ */
  pvMonstre: number
  pvMonstreMax: number
  /** butin accumulé depuis l'entrée, remis à la sortie */
  butin: Record<number, number>
  /** monstres abattus depuis l'entrée */
  vaincus: number
  /**
   * Millisecondes écoulées depuis le dernier coup porté.
   *
   * La vie du monstre descendait de façon **continue**, en glissant, là où un
   * combat se lit coup par coup. Les dégâts tombent donc par paquets, à la
   * cadence d'une frappe, sans changer le temps total : l'intervalle vaut la
   * durée du combat divisée par le nombre de coups qu'il faut pour l'abattre.
   */
  depuisCoup: number
  /** dégâts d'une frappe sur le monstre au contact */
  degatCoup: number
  /** intervalle entre deux frappes sur ce monstre, en ms */
  cadenceMs: number
  /** ce que la descente a rapporté, pour le bilan de sortie */
  xp: number
  dureeS: number
  vivres: number
}

export interface Personnage {
  id: string
  nom: string
  /** id de classe Dofus (breeds.json) */
  classe: number
  niveau: number
  xp: number
  /** points de caractéristique répartis (GDD §3) */
  carac: { vitalite: number; force: number; intelligence: number; chance: number; agilite: number; sagesse: number }
  /** niveaux de métier, PAR personnage, c'est le pilier du multi-compte (GDD §9) */
  metiers: Record<number, { niveau: number; xp: number }>
  /** objet porté par emplacement, avec le jet obtenu au craft */
  equipement: Partial<Record<Slot, Porte>>
  pv: number
  activite: Activite | null
  /** monstre actuellement combattu (la famille est la cible, le monstre tourne) */
  cible: number | null
  /** temps déjà passé sur la cible, en ms */
  tempsSurCible: number
  /** points de caractéristique non dépensés */
  pointsLibres: number
  /** compteurs d'affichage */
  kills: number
  /**
   * Butin ramassé pendant le **dernier pas de simulation**, par ce personnage.
   *
   * L'inventaire est commun au compte : sans cette attribution, le journal,
   * qui se déduit par différence d'une image à l'autre, montrait le butin de
   * tous les personnages dans celui qu'on regardait. Purement informatif : ce
   * n'est pas une réserve, le butin est déjà rangé dans l'inventaire.
   */
  dernierButin?: Record<number, number>
  /** true quand le personnage est mort : le farm est arrêté jusqu'à un clic (GDD §4) */
  arrete: boolean
  /** progression dans le donjon en cours, absente hors descente */
  descente?: EnDescente | null
  /**
   * Descentes encore à enchaîner après celle-ci.
   *
   * « C'est un peu chiant de devoir cliquer sur recommencer » : on lance une
   * série, elle se déroule seule tant qu'il reste des clés **et** que le
   * personnage tient debout. Une défaite l'interrompt — sinon cinquante clés
   * partiraient sur un personnage à terre.
   */
  enchaine?: number
  /**
   * Bilan cumulé de la série en cours.
   *
   * On ne remonte pas un bilan par descente — ce serait une bannière toutes les
   * deux minutes, exactement ce qu'on vient de corriger. Les chiffres
   * s'additionnent ici et ne sortent qu'à la fin de la série.
   */
  descenteCumul?: import("./donjon.ts").Descente | null
  /**
   * Millisecondes restantes avant de pouvoir remanger.
   *
   * Un temps **restant**, pas une date : le pas de simulation avance par
   * tranches de 250 ms sans faire avancer `lastTick`, si bien qu'un horodatage
   * comparé à l'horloge aurait été faux pendant tout le rattrapage.
   */
  repasDans?: number
}

export interface Etat {
  /** version du schéma d'état, pour les migrations */
  schema: number
  /** horodatage du dernier tick appliqué, le cœur du modèle (GDD §14.1) */
  lastTick: number
  /** graine du générateur pseudo-aléatoire : rend la simulation reproductible */
  seed: number

  // --- partagé au compte (GDD §9)
  kamas: number
  /** Lignes remontées à la forge, pour les succès. */
  forges?: number
  /** Dernière ligne du journal du marché déjà appliquée (GDD §20). */
  hdvCurseur?: number
  /** Crans arrachés au-dessus du maximum naturel, à la forge. */
  surpuissances?: number
  inventaire: Record<number, number>
  zones: number[]
  archimonstres: number[]
  recherches: number[]
  /** archimonstre visible en ce moment ; disparaît s'il expire (GDD §10) */
  apparition: import("./collection.ts").Apparition | null
  /** nombre d'archimonstres ratés faute de présence */
  archiRates: number
  /** avis de recherche du jour, quand une rencontre vient d'avoir lieu */
  dernierRecherche: { monstre: number; vaincu: boolean } | null
  /** fin du bonus d'Almanax, en horodatage ; null si aucun bonus en cours */
  almanaxJusqua: number | null
  /** jour de la dernière offrande, pour ne pas la refaire deux fois */
  almanaxJour: string | null
  dofus: number[]
  /** boss de donjon déjà vaincus, pour le Dofus Pourpre */
  bossVaincus: number[]
  /** meilleur % de perfection obtenu sur chaque objet */
  recordsCraft: Record<number, number>
  /**
   * Équipement possédé mais non porté. Déséquiper y renvoie la pièce au lieu
   * de la détruire. L'inventaire de ressources, lui, ne sait compter que des
   * quantités et perdrait le jet.
   */
  coffre: Porte[]
  /**
   * Recettes mises de côté : « je veux fabriquer ça ».
   *
   * Elles remontent en tête de l'atelier quels que soient la recherche et le
   * filtre : c'est l'objectif qu'on suit, pas un résultat de liste.
   */
  epingles: number[]
  /** compte rendu de la dernière descente, pour l'affichage */
  derniereDescente: import("./donjon.ts").Descente | null
  /** jet fraîchement crafté, en attente de décision. On ne remplace jamais
   *  l'équipement sans montrer le résultat d'abord (GDD §6) */
  enAttente: Porte | null

  // --- par personnage
  /**
   * Victoires par famille de monstres, pour tout le compte.
   *
   * `Personnage.kills` ne donne qu'un total : « abattre mille Bouftous » ne
   * pouvait pas se mesurer. Le compteur est commun à l'équipe — c'est le joueur
   * qu'on récompense, pas un personnage en particulier.
   */
  killsFamille?: Record<number, number>
  /**
   * Succès obtenus, et quand. **Un succès ne se reperd jamais.**
   *
   * Ils se mesuraient sur l'état courant, ce qui allait tant que la mesure ne
   * pouvait que monter — mais la fortune se dépense et le coffre se vide :
   * atteindre cent mille kamas décrochait le succès, les dépenser le reprenait.
   * Le moteur inscrit donc ici ce qui est acquis, et l'écran lit cette liste.
   */
  succes?: Record<string, number>
  /**
   * Panoplies dont un palier a été actif au moins une fois.
   *
   * On ne porte qu'un équipement à la fois : compter les panoplies **actives**
   * rendait « trois panoplies » impossible, puisque changer de panoplie fait
   * retomber le compte à une. Ce qu'on récompense, c'est d'en avoir complété
   * plusieurs, pas de les porter ensemble.
   */
  panopliesVues?: number[]
  personnages: Personnage[]
  /** id du personnage affiché et ciblé par les actions */
  persoActif: string | null
}

export const SCHEMA = 1

/**
 * Complète un état venu d'une version antérieure.
 *
 * Sans ça, chaque champ ajouté au schéma casse toutes les sauvegardes en
 * circulation : les nouveaux champs valent `undefined`, et le moindre garde
 * écrit `!== null` laisse passer. On repart donc systématiquement d'un état
 * neuf sur lequel on superpose ce qui existe.
 */
export function migrer(brut: Partial<Etat> | null | undefined, now: number, seed: number): Etat {
  const neuf = etatInitial(now, seed)
  if (!brut) return neuf
  let compteur = 0
  const identifie = <T extends { uid?: string; item?: number }>(x: T): T =>
    x && !x.uid ? { ...x, uid: `m${Date.now().toString(36)}${compteur++}` } : x

  /**
   * Une sauvegarde peut arriver abîmée : disque corrompu, réponse tronquée,
   * fichier bricolé à la main. Un champ de liste qui n'est pas une liste faisait
   * mourir l'application sur un écran blanc, sans aucun recours pour le joueur.
   * On préfère repartir de la valeur neuve : perdre un champ vaut mieux que
   * perdre l'accès au jeu.
   */
  const liste = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : [])
  const objet = <T extends object>(v: unknown, defaut: T): T =>
    v && typeof v === "object" && !Array.isArray(v) ? (v as T) : defaut
  const nombre = (v: unknown, defaut: number) => (typeof v === "number" && Number.isFinite(v) ? v : defaut)

  const personnages = liste<Personnage>(brut.personnages).map((p) => ({
    ...persoNeuf(),
    ...p,
    carac: { ...persoNeuf().carac, ...objet(p.carac, {}) },
    metiers: objet(p.metiers, {}),
    // Les pièces portées d'avant le passage aux instances n'ont pas
    // d'identifiant : sans lui, on peut les déséquiper mais plus jamais les
    // remettre. On leur en attribue un à la volée.
    equipement: Object.fromEntries(
      Object.entries(objet(p.equipement, {})).filter(([, v]) => v).map(([k, v]) => [k, identifie(v as never)]),
    ) as typeof p.equipement,
  }))
  return {
    ...neuf,
    ...brut,
    personnages,
    schema: SCHEMA,
    inventaire: objet(brut.inventaire, {}),
    recordsCraft: objet(brut.recordsCraft, {}),
    coffre: liste<Porte>(brut.coffre).filter(Boolean).map((x) => identifie(x)),
    epingles: liste<number>(brut.epingles).filter((x) => typeof x === "number"),
    zones: liste<number>(brut.zones),
    archimonstres: liste<number>(brut.archimonstres),
    recherches: liste<number>(brut.recherches),
    dofus: liste<number>(brut.dofus),
    bossVaincus: liste<number>(brut.bossVaincus),
    kamas: Math.max(0, nombre(brut.kamas, 0)),
    forges: Math.max(0, nombre(brut.forges, 0)),
    hdvCurseur: Math.max(0, nombre(brut.hdvCurseur, 0)),
    surpuissances: Math.max(0, nombre(brut.surpuissances, 0)),
    lastTick: nombre(brut.lastTick, now),
    seed: nombre(brut.seed, seed) >>> 0,
    apparition: brut.apparition ?? null,
    // Les descentes d'avant le donjon jouable avaient une autre forme — une
    // liste de salles au lieu d'un bilan. On les jette plutôt que d'afficher
    // un compte rendu vide au premier chargement.
    derniereDescente: typeof (brut.derniereDescente as { salles?: unknown } | null)?.salles === "number"
      ? brut.derniereDescente ?? null : null,
    dernierRecherche: brut.dernierRecherche ?? null,
    enAttente: brut.enAttente ? identifie(brut.enAttente) : null,
    archiRates: Math.max(0, nombre(brut.archiRates, 0)),
    almanaxJusqua: brut.almanaxJusqua ?? null,
    almanaxJour: brut.almanaxJour ?? null,
    killsFamille: objet(brut.killsFamille, {}) as Record<number, number>,
    succes: objet(brut.succes, {}) as Record<string, number>,
    panopliesVues: liste<number>(brut.panopliesVues),
    persoActif: brut.persoActif ?? personnages[0]?.id ?? null,
  }
}

const persoNeuf = (): Personnage => ({
  id: "", nom: "", classe: 8, niveau: 1, xp: 0,
  carac: { vitalite: 0, force: 0, intelligence: 0, chance: 0, agilite: 0, sagesse: 0 },
  metiers: {}, equipement: {}, pv: 0, activite: null, arrete: false,
  cible: null, tempsSurCible: 0, pointsLibres: 0, kills: 0, descente: null, repasDans: 0,
})

export function etatInitial(now: number, seed: number): Etat {
  return {
    schema: SCHEMA,
    lastTick: now,
    seed,
    kamas: 0,
    forges: 0,
    hdvCurseur: 0,
    surpuissances: 0,
    inventaire: {},
    zones: [],
    archimonstres: [],
    recherches: [],
    apparition: null,
    archiRates: 0,
    dernierRecherche: null,
    almanaxJusqua: null,
    almanaxJour: null,
    dofus: [],
    bossVaincus: [],
    recordsCraft: {},
    coffre: [],
    epingles: [],
    enAttente: null,
    derniereDescente: null,
    killsFamille: {},
    succes: {},
    panopliesVues: [],
    personnages: [],
    persoActif: null,
  }
}
